import { Menu, SegmentedControl, Switch, useMantineColorScheme } from '@mantine/core';
import {
  IconBeach,
  IconChevronDown,
  IconContrast,
  IconDevices,
  IconHelp,
  IconKey,
  IconLogout,
  IconMailFast,
  IconUser,
} from '@tabler/icons-react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMyHolidayPrefs, useSetMyHolidayPrefs } from '../../api/holidays';
import { hasPermission } from '../access';
import { toast } from '../toast';
import { Avatar } from '../../components/Avatar';
import { PoweredBy } from '../../components/PoweredBy';
import { useSession } from '../session';
import { useIsMobile } from '../useIsMobile';
import classes from './shell.module.css';

/**
 * Account menu: profile, my holidays, devices, password, the daily who's-off email switch
 * (managers with scope view, M4), theme, help, sign out, and the AutoMM credit.
 */
export function UserMenu() {
  const { user, signOut, access } = useSession();
  const getsDigest = hasPermission(access, 'holidays.scope.view');
  const prefs = useMyHolidayPrefs(getsDigest);
  const setPrefs = useSetMyHolidayPrefs();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [opened, setOpened] = useState(false);
  const { colorScheme, setColorScheme } = useMantineColorScheme();
  const scheme = colorScheme === 'light' ? 'light' : 'dark';

  const onSignOut = async () => {
    await signOut();
    navigate('/login', { replace: true });
  };

  return (
    <Menu
      opened={opened}
      onChange={setOpened}
      width={isMobile ? 'calc(100vw - 16px)' : 300}
      position="bottom-end"
      offset={4}
      shadow="md"
      withinPortal
    >
      <Menu.Target>
        <button
          type="button"
          className={classes.userButton}
          data-open={opened || undefined}
          aria-label="Your account"
          aria-expanded={opened}
        >
          <Avatar initials={user.initials} me size={32} />
          {!isMobile && (
            <>
              <span className={classes.userText}>
                <span className={classes.userName}>{user.name}</span>
                <span className={classes.userRole}>{user.roleTitle}</span>
              </span>
              <IconChevronDown size={16} stroke={2} className={classes.mutedIcon} />
            </>
          )}
        </button>
      </Menu.Target>
      <Menu.Dropdown>
        <div className={classes.menuUser}>
          <Avatar initials={user.initials} me size={40} />
          <div className={classes.menuUserText}>
            <span className={classes.menuUserName}>{user.name}</span>
            <span className={classes.menuUserRole}>
              {user.roleTitle}
              {user.homeBranch ? ` · ${user.homeBranch}` : ''}
            </span>
          </div>
        </div>
        <Menu.Divider />
        <Menu.Item className={classes.menuItem} leftSection={<IconUser size={18} stroke={2} />}>
          My profile
        </Menu.Item>
        <Menu.Item
          className={classes.menuItem}
          leftSection={<IconBeach size={18} stroke={2} />}
          onClick={() => navigate('/hr/holiday')}
        >
          My holidays
        </Menu.Item>
        <Menu.Item
          className={classes.menuItem}
          leftSection={<IconDevices size={18} stroke={2} />}
          onClick={() => navigate('/account/devices')}
        >
          Signed-in devices
        </Menu.Item>
        <Menu.Item
          className={classes.menuItem}
          leftSection={<IconKey size={18} stroke={2} />}
          onClick={() => navigate('/account/password')}
        >
          Change password
        </Menu.Item>
        {getsDigest && (
          <div className={classes.menuThemeRow}>
            <span className={classes.menuThemeLabel} id="digest-label">
              <IconMailFast size={18} stroke={2} />
              Daily who's-off email
            </span>
            <Switch
              size="sm"
              color="spartan"
              aria-labelledby="digest-label"
              checked={prefs.data ? !prefs.data.dailyDigestOptOut : false}
              disabled={!prefs.data || setPrefs.isPending}
              onChange={(e) => {
                const on = e.currentTarget.checked;
                setPrefs.mutate(
                  { dailyDigestOptOut: !on },
                  {
                    onSuccess: () =>
                      toast.ok(
                        on ? 'Daily email on' : 'Daily email off',
                        on
                          ? "You'll get who's off at 7 each morning."
                          : "You won't get the morning who's-off email.",
                      ),
                    onError: () => toast.error("Couldn't save that", 'Try again in a moment.'),
                  },
                );
              }}
            />
          </div>
        )}
        <div className={classes.menuThemeRow}>
          <span className={classes.menuThemeLabel} id="theme-label">
            <IconContrast size={18} stroke={2} />
            Theme
          </span>
          <SegmentedControl
            size="xs"
            color="spartan"
            value={scheme}
            onChange={(v) => setColorScheme(v === 'light' ? 'light' : 'dark')}
            data={[
              { value: 'dark', label: 'Dark' },
              { value: 'light', label: 'Light' },
            ]}
            aria-labelledby="theme-label"
          />
        </div>
        <Menu.Item className={classes.menuItem} leftSection={<IconHelp size={18} stroke={2} />}>
          Help and guides
        </Menu.Item>
        <Menu.Item
          className={classes.menuItem}
          leftSection={<IconLogout size={18} stroke={2} />}
          onClick={() => void onSignOut()}
        >
          Sign out
        </Menu.Item>
        <Menu.Divider />
        <div className={classes.menuFooter}>
          <PoweredBy on="surface" prefix="Aroo v2.0 · Powered by" />
        </div>
      </Menu.Dropdown>
    </Menu>
  );
}
